/* ──────────────────────────────────────────────────────────────────────
   /what-you-earn — Public commission disclosure page.

   Shows every affiliate program on Faitharama with the EXACT percentage
   the member keeps. Pulled from the same STORES list as /market so the
   numbers stay in sync — change a memberPct in MallMarketPage.jsx and it
   updates here automatically.

   No signup required to view. The point: let people see the money BEFORE
   they join.
   ────────────────────────────────────────────────────────────────────── */
import { Link } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { DollarSign, ExternalLink, ShieldCheck } from "lucide-react";

const G    = "#2E9F3A";
const O    = "#F39A1F";
const RED  = "#C9202E";
const INK  = "#0F172A";
const SKY  = "#EAF6FC";

const G_AD             = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/4jq13qjg_goli.com%20gummies.png";
const CC_AD            = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/4pj7t8vl_Screenshot%202026-06-14%20215258.png";
const DETOX_AD         = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/2abfkak8_DETOX%20ORGANICS.png";
const ERA_AD           = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/0h4sdtai_ERA%20ORGANIC%20MOMS%20AND%20KIDS.png";
const BEE_AD           = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/o08i64t2_Screenshot%202026-06-06%20125500.png";
const HEMP_AD          = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/ebulo6f5_Screenshot%202026-06-09%20171454.png";
const PIURIFY_AD       = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/xg6blyqf_Screenshot%202026-05-13%20134456.png";
const GARDEN_AD        = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/v007eedq_Screenshot%202026-06-08%20221226.png";
const UPN_AD           = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/xzkog6h6_UPNOURISH%20AD.png";
const TM_AD            = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/jgtmpxxd_Screenshot%202026-06-09%20215401.png";
const RC_AD            = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/jqkt2k8o_Screenshot%202026-06-09%20105917.png";
const BOOK_AD          = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/qe4t13t2_WITH%20FAITH%20ALL%20THINGS%20ARE%20POSSIBLE%20BOOK%2C%20COURSE%2C%20AND%20AUDIO.png";
const ACADEMY_AD       = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/50opfc2b_THE%20COMPLETE%20FAITHARAMA.COM%20TOOLKIT%20GUIDE.png";
const AFF101_AD        = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/7mqgj7qx_1000%20dollars%20in%2090%20days.png";

/* Source of truth for what every member earns. Mirrors the STORES array
   inside MallMarketPage.jsx. Update both when adding/removing brands. */
const PAYOUTS = [
  { name: "Goli",                pct: 7.5,  goes: "100% to member (no split)",            note: "Apple Cider Vinegar Gummies", payout: "Goli pays the member directly. Lifetime tracking.", img: G_AD },
  { name: "ChristianCafe.com",   pct: 40,   goes: "100% to member (Yazing direct)",       note: "Christian dating · 27 years", payout: "Yazing pays the member directly. No middleman.",   img: CC_AD },
  { name: "Detox Organics",      pct: 15,   goes: "100% to member (Yazing direct)",       note: "Organic superfood powders",   payout: "Yazing pays the member directly.",                 img: DETOX_AD },
  { name: "Era Organics",        pct: 10,   goes: "100% to member (Yazing direct)",       note: "Clean beauty · Mom & Kids",   payout: "Yazing pays the member directly.",                 img: ERA_AD },
  { name: "Bee & You",           pct: 10,   goes: "50/50 split (member / Faitharama)",    note: "Raw honey · Royal jelly",     payout: "Faitharama collects, splits 50/50 with member.",   img: BEE_AD },
  { name: "GoGreen Hemp",        pct: 7.5,  goes: "50/50 split (member / Faitharama)",    note: "Broad-spectrum CBD",          payout: "Faitharama collects, splits 50/50 with member.",   img: HEMP_AD },
  { name: "Piurify",             pct: 5,    goes: "50/50 split (member / Faitharama)",    note: "Clean water filtration",      payout: "Faitharama collects, splits 50/50 with member.",   img: PIURIFY_AD },
  { name: "5-Minute Garden",     pct: 37.5, goes: "50/50 split (member / Faitharama)",    note: "Grow food in 100 sq ft",      payout: "Faitharama collects, splits 50/50 with member.",   img: GARDEN_AD },
  { name: "UpNourish",           pct: 5,    goes: "50/50 split (member / Faitharama)",    note: "Plant protein · GLP-1",       payout: "Faitharama collects, splits 50/50 with member.",   img: UPN_AD },
  { name: "WellFit Treadmills",  pct: 2.5,  goes: "50/50 split (member / Faitharama)",    note: "TM029 Pro · Senior-friendly", payout: "Faitharama collects, splits 50/50 with member.",   img: TM_AD },
  { name: "BeachRC.com",         pct: 12.5, goes: "50/50 split (member / Faitharama)",    note: "RC race buggies",             payout: "Faitharama collects, splits 50/50 with member.",   img: RC_AD },
  { name: "Faitharama Books",       pct: 50, goes: "50/50 split (member / Faitharama)",   note: "With Faith All Things Are Possible", payout: "Faitharama pays the member out monthly.",  img: BOOK_AD },
  { name: "Faitharama Academy",     pct: 50, goes: "50/50 split (member / Faitharama)",   note: "Online courses",               payout: "Faitharama pays the member out monthly.",          img: ACADEMY_AD },
  { name: "Affiliate Marketing 101",pct: 50, goes: "50/50 split (member / Faitharama)",   note: "90-day playbook",              payout: "Faitharama pays the member out monthly.",          img: AFF101_AD },
];

export default function WhatYouEarnPage() {
  return (
    <div className="min-h-screen bg-ivory">
      <SiteNav />

      {/* HERO */}
      <section className="px-4 md:px-8 py-10 md:py-14" style={{ background: INK, color: "#FFFFFF" }}>
        <div className="max-w-5xl mx-auto">
          <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-3" style={{ color: O }}>
            ★ WHAT YOU EARN ON EVERY BRAND
          </div>
          <h1 className="font-serif-display text-4xl md:text-6xl leading-[1.05] mb-4">
            See The Money <span style={{ color: G }}>Before</span> You Sign Up.
          </h1>
          <p className="text-base md:text-lg max-w-2xl" style={{ color: SKY }}>
            Faitharama members earn real affiliate income on every brand in our market.
            Below is the exact percentage you take home on each one. No hidden fine print,
            no &quot;up to,&quot; no &quot;tiered bonuses you&apos;ll never hit.&quot; This is what you get.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/signup" data-testid="wye-cta-signup"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm uppercase tracking-[0.2em] font-bold hover:scale-[1.03] transition shadow-lg"
                  style={{ background: O, color: "#FFFFFF" }}>
              <DollarSign className="w-4 h-4"/> Start Earning · Free To Join
            </Link>
            <Link to="/market" data-testid="wye-cta-market"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm uppercase tracking-[0.2em] font-bold hover:scale-[1.03] transition"
                  style={{ background: "transparent", color: "#FFFFFF", border: "2px solid #FFFFFF" }}>
              <ExternalLink className="w-4 h-4"/> See The Market
            </Link>
          </div>
        </div>
      </section>

      {/* PAYOUT TABLE */}
      <section className="px-4 md:px-8 py-10 md:py-12">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" style={{ color: G }}/>
            <p className="text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: G }}>
              Every number below is the percentage of the sale that ends up in the member&apos;s pocket.
            </p>
          </div>

          <div className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: `${G}33`, background: "#FFFFFF" }}>
            <div className="px-5 py-3 border-b-2 grid grid-cols-12 text-[10px] tracking-[0.18em] uppercase font-bold"
                 style={{ background: SKY, borderColor: `${G}33`, color: INK }}>
              <div className="col-span-5">Brand</div>
              <div className="col-span-3 text-right">You Earn</div>
              <div className="col-span-4">How You Get Paid</div>
            </div>
            {PAYOUTS.map((p, i) => (
              <div key={p.name} data-testid={`wye-row-${i}`}
                   className="px-5 py-4 grid grid-cols-12 items-start gap-2 border-t"
                   style={{ borderColor: "#E5E7EB" }}>
                <div className="col-span-5">
                  <div className="font-bold text-base" style={{ color: INK }}>{p.name}</div>
                  <div className="text-xs mt-0.5" style={{ color: INK, opacity: 0.7 }}>{p.note}</div>
                </div>
                <div className="col-span-3 text-right">
                  <div className="text-3xl md:text-4xl font-bold tabular-nums leading-none" style={{ color: G }}>
                    {p.pct}<span className="text-xl">%</span>
                  </div>
                  <div className="text-[10px] tracking-[0.18em] uppercase font-bold mt-1" style={{ color: O }}>
                    Per Sale
                  </div>
                </div>
                <div className="col-span-4">
                  <div className="text-xs font-bold uppercase tracking-[0.14em] mb-1" style={{ color: INK }}>
                    {p.goes}
                  </div>
                  <div className="text-xs leading-snug" style={{ color: INK, opacity: 0.75 }}>
                    {p.payout}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* HONESTY NOTE */}
          <div className="mt-8 rounded-xl border-2 p-5 md:p-6" style={{ borderColor: `${RED}55`, background: "#FFF5F5" }}>
            <h3 className="text-sm tracking-[0.22em] uppercase font-bold mb-2" style={{ color: RED }}>
              ★ The Honest Truth
            </h3>
            <ul className="text-sm space-y-1.5" style={{ color: INK }}>
              <li>• Earnings depend on how many people click your link and buy. We don&apos;t guarantee a dollar amount.</li>
              <li>• Some brands (Goli, Yazing programs) pay the member directly. Others Faitharama collects and splits 50/50.</li>
              <li>• 50/50 splits are paid out on the 1st and 15th of every month once the buyer&apos;s payment has cleared.</li>
              <li>• Nothing here is a get-rich-quick scheme. It&apos;s a real affiliate income tool — work it, earn money.</li>
            </ul>
          </div>

          <div className="mt-8 text-center">
            <Link to="/signup" data-testid="wye-bottom-cta"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full text-sm uppercase tracking-[0.22em] font-bold hover:scale-[1.03] transition shadow-lg"
                  style={{ background: G, color: "#FFFFFF" }}>
              <DollarSign className="w-4 h-4"/> Join Free — Get Your Affiliate Links
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
